import * as React from "react";

import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

import { AmplitudeEvents } from "../../constants/amplitude";
import { useAmplitude } from "../../hooks/amplitude/useAmplitude";
import { NextLink } from "../NextLink/NextLink";

import { DashboardButtonProps, DashboardButton } from "@/dashboard-design-system/Button";
import { DashboardDivider } from "@/dashboard-design-system/Divider/DashboardDivider";
import { DashboardTooltip, DashboardTooltipProps } from "@/dashboard-design-system/Tooltip";
import { ChevronDown } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

type PopperPlacement =
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "left"
  | "left-start"
  | "left-end";

export type DropdownButtonProps = {
  size?: DashboardButtonProps["size"];
  variant?: DashboardButtonProps["variant"];
  vibe?: DashboardButtonProps["vibe"];
  buttonText?: string;
  onlyIcon?: IconElement;
  startIcon?: IconElement;
  endIcon?: IconElement;
  menuItemsProps: Array<
    Partial<Omit<MenuItemProps, "button" | "onClick">> & {
      key: string;
      children: React.ReactNode;
      href?: string;
      onClick?: (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => void;
      tooltipProps?: Pick<DashboardTooltipProps, "title" | "placement">;
      "data-test"?: string;
      target?: string;
      isLoading?: boolean;
      disabled?: boolean;
    }
  >;
  isMobileLayout?: boolean;
  amplitudeEventOnClick?: AmplitudeEvents;
  onClick?: (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => void;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  "data-test"?: string;
  disabled?: boolean;
  placement?: PopperPlacement;
};

export interface DropdownButtonRef {
  setOpen: (input: boolean) => void;
}

export const DropdownButton = React.forwardRef<DropdownButtonRef, DropdownButtonProps>(
  (
    {
      buttonText,
      menuItemsProps,
      vibe = "brand",
      variant = "filled",
      size,
      startIcon,
      onlyIcon,
      endIcon,
      isMobileLayout,
      amplitudeEventOnClick,
      onClick,
      buttonRef,
      "data-test": dataTest,
      disabled,
      placement = "bottom",
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const { logAmplitudeEvent } = useAmplitude();

    const handleToggle = React.useCallback(
      (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => {
        if (onClick) {
          onClick(event);
        }
        setOpen((prevOpen) => !prevOpen);
        if (amplitudeEventOnClick) {
          logAmplitudeEvent(amplitudeEventOnClick);
        }
      },
      [amplitudeEventOnClick, logAmplitudeEvent, onClick]
    );

    const onClickOnAMenuButton = React.useCallback(() => {
      setOpen(false);
    }, []);

    const handleClose = React.useCallback((event: MouseEvent | TouchEvent) => {
      if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
        return;
      }
      setOpen(false);
    }, []);

    React.useImperativeHandle(
      ref,
      () => ({
        setOpen: (input: boolean) => {
          setOpen(input);
        },
      }),
      []
    );

    if (isMobileLayout) {
      return (
        <>
          {menuItemsProps.map((prop) => {
            const { children, key, onClick, "data-test": dataTest, href, ..._restProp } = prop;
            return (
              <Grid item key={key}>
                <DashboardDivider />
                <DashboardButton
                  onClick={onClick}
                  variant="text"
                  vibe="neutral"
                  fullWidth
                  sx={{ justifyContent: "flex-start", pl: 0 }}
                  data-test={dataTest}
                  href={href}
                  target={prop.target}
                >
                  {children}
                </DashboardButton>
              </Grid>
            );
          })}
        </>
      );
    }

    return (
      <>
        <ButtonGroup variant="contained" ref={anchorRef} aria-label="split button" fullWidth sx={{ boxShadow: "none" }}>
          <DashboardButton
            vibe={vibe}
            variant={variant}
            size={size}
            aria-controls={open ? "split-button-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleToggle}
            startIcon={onlyIcon ?? startIcon}
            endIcon={endIcon ?? (onlyIcon ? undefined : <ChevronDown />)}
            data-test={dataTest}
            ref={buttonRef}
            disabled={disabled}
            fullWidth
          >
            {!onlyIcon && buttonText}
          </DashboardButton>
        </ButtonGroup>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          onClick={onClickOnAMenuButton}
          style={{ zIndex: 10000 }}
          placement={placement}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement.includes("bottom")
                  ? "center top"
                  : placement.includes("top")
                    ? "center bottom"
                    : placement.includes("right")
                      ? "left center"
                      : "right center",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    {menuItemsProps.map((prop) => {
                      const {
                        children,
                        key,
                        href,
                        "data-test": dataTest,
                        tooltipProps,
                        target,
                        isLoading,
                        disabled,
                        ...restProp
                      } = prop;
                      const currentDisabled = disabled || isLoading;
                      if (href) {
                        return (
                          <NextLink href={href} target={target}>
                            <MenuItem key={key} disabled={currentDisabled} {...restProp} data-test={dataTest}>
                              {children}
                            </MenuItem>
                          </NextLink>
                        );
                      }
                      if (tooltipProps) {
                        return (
                          <DashboardTooltip key={key} {...tooltipProps}>
                            <span>
                              <MenuItem disabled={currentDisabled} {...restProp}>
                                {children}
                              </MenuItem>
                            </span>
                          </DashboardTooltip>
                        );
                      }
                      return (
                        <MenuItem key={key} disabled={currentDisabled} {...restProp} data-test={dataTest}>
                          {children}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  }
);

DropdownButton.displayName = "DropdownButton";
